import { useLogto } from '@logto/react';
import { yes } from '@silverhand/essentials';
import { useContext, useEffect, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';

import { useCloudApi } from '@/cloud/hooks/use-cloud-api';
import AppLoading from '@/components/AppLoading';
import { searchKeys } from '@/consts';
import { TenantsContext } from '@/contexts/TenantsProvider';
import useConsoleSignIn from '@/hooks/use-console-sign-in';
import { hasPortalConnector, needsConsoleAccountCheck } from '@/utils/portal-session';
import { saveRedirect } from '@/utils/storage';

/**
 * The container for all protected routes. It renders `<AppLoading />` when the user is not
 * authenticated or the user is authenticated but the tenant is not initialized.
 *
 * That is, when it renders `<Outlet />`, you can expect:
 *
 * - `isAuthenticated` from `useLogto()` to be `true`.
 * - `isInitComplete` from `TenantsContext` to be `true`.
 *
 * Usage:
 *
 * ```tsx
 * <Route element={<ProtectedRoutes />}>
 *  <Route path="some-path" element={<SomeContent />} />
 * </Route>
 * ```
 *
 * Note that the `ProtectedRoutes` component should be put in a {@link https://reactrouter.com/en/main/start/concepts#pathless-routes | pathless route}.
 */
export default function ProtectedRoutes() {
  const api = useCloudApi();
  const [searchParameters] = useSearchParams();
  const { isAuthenticated, isLoading } = useLogto();
  const { isInitComplete, resetTenants } = useContext(TenantsContext);
  const signIn = useConsoleSignIn();
  const [checkAccount, setCheckAccount] = useState(needsConsoleAccountCheck);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (isLoading || (isAuthenticated && !checkAccount)) {
      return;
    }

    const controller = new AbortController();
    const startSignIn = async () => {
      try {
        const federated = checkAccount && (await hasPortalConnector());
        if (controller.signal.aborted) {
          return;
        }
        if (isAuthenticated && !federated) {
          setCheckAccount(false);
          return;
        }
        saveRedirect();
        await signIn(yes(searchParameters.get(searchKeys.signUp)));
      } catch (error_: unknown) {
        if (!controller.signal.aborted) {
          setError(error_ instanceof Error ? error_ : new Error('Could not start console sign-in'));
        }
      }
    };
    void startSignIn();

    return () => {
      controller.abort();
    };
  }, [checkAccount, isAuthenticated, isLoading, searchParameters, signIn]);

  useEffect(() => {
    if (!checkAccount && isAuthenticated && !isInitComplete) {
      const loadTenants = async () => {
        const data = await api.get('/api/tenants');
        resetTenants(data);
      };

      void loadTenants();
    }
  }, [api, checkAccount, isAuthenticated, isInitComplete, resetTenants]);

  if (error) {
    throw error;
  }

  if (checkAccount || !isInitComplete || !isAuthenticated) {
    return <AppLoading />;
  }

  return <Outlet />;
}
